/**
 * AdminScraperTab - Event scraper and management tab
 * Self-contained component for scraping events from external sources
 * Extracted from AdminPage.js for better maintainability
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  RefreshCw, Download, Clock, Trash2, MapPin, CheckCircle, XCircle,
  Calendar, Globe, Link2, AlertTriangle, Upload, ExternalLink, X,
  ChevronLeft, ChevronRight, Pencil, Eye, EyeOff, Check, Loader2, Copy
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminScraperTab({ clubs = [], onDataChange }) {
  // State
  const [scrapedEvents, setScrapedEvents] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scraperMonth, setScraperMonth] = useState('march');
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [eventSourceFilter, setEventSourceFilter] = useState('all');
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  
  // Selection
  const [selectedScrapedEvents, setSelectedScrapedEvents] = useState(new Set());
  
  // Duplicates
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);
  
  // Auto-linking
  const [linkSuggestions, setLinkSuggestions] = useState([]);
  const [showLinkSuggestions, setShowLinkSuggestions] = useState(false);
  const [linkingEvents, setLinkingEvents] = useState(false);
  
  // Edit modal
  const [editingEvent, setEditingEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    price: '',
    tag: 'Community',
    image_url: '',
    linked_club_id: '',
    ticket_url: '',
    ticket_widget_code: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [customImageUploading, setCustomImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 20;
  
  // Event tags
  const eventTags = ['Community', 'Featured', 'Workshop', 'Special Event', 'Festival', 'Competition', 'Social Dance'];
  
  // Default event images
  const defaultEventImages = [
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/65841c5637202b42d435c0af23763090f52817addef14b1273ea06968844e5ee.png',
      label: 'Line Dancers Social'
    },
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/8a95a2f54c28f0b55b80f3a0b9aa99ffed15e69cd2e5e9ead6e89d1ed46cf76e.png',
      label: 'Dance Event'
    },
    {
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/3b9b2db2f1dce8ce0f8b8b8a9c4adbbab2d93a7ee9e5c16a6de07c4f33e2e7a8.png',
      label: 'Country Dance'
    }
  ];

  // Fetch data on mount
  useEffect(() => {
    fetchScrapedEvents();
    fetchSchedulerStatus();
  }, []);

  const fetchScrapedEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setScrapedEvents(data);
      }
    } catch (error) {
      console.error('Error fetching scraped events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraper/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const runScheduledScrape = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraper/run-now`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Scheduled scrape completed');
        fetchScrapedEvents();
        fetchSchedulerStatus();
        if (onDataChange) onDataChange();
      } else {
        toast.error(data.detail || 'Failed to run scheduled scrape');
      }
    } catch (error) {
      console.error('Scheduled scrape error:', error);
      toast.error(`Error running scheduled scrape: ${error.message || 'Network error'}`);
    } finally {
      setScraping(false);
    }
  };

  const runScraper = async (month = null) => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      let url;
      
      if (month) {
        url = `${API_URL}/api/admin/scraper/run-month?month=${month.toLowerCase()}&year=2026`;
      } else {
        url = `${API_URL}/api/admin/scraper/run?year=2026`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || `Scraping completed`);
        fetchScrapedEvents();
        if (onDataChange) onDataChange();
      } else {
        toast.error(data.detail || 'Scraping failed');
      }
    } catch (error) {
      console.error('Scraper error:', error);
      toast.error(`Error running scraper: ${error.message || 'Network error'}`);
    } finally {
      setScraping(false);
    }
  };

  const runBackroadBootsScraper = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/scraper/backroad-boots`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Backroad Boots scraping completed');
        fetchScrapedEvents();
        if (onDataChange) onDataChange();
      } else {
        toast.error(data.detail || 'Backroad Boots scraping failed');
      }
    } catch (error) {
      console.error('Backroad Boots scraper error:', error);
      toast.error(`Error running Backroad Boots scraper: ${error.message || 'Network error'}`);
    } finally {
      setScraping(false);
    }
  };

  const geocodeAllEvents = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/geocode-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Geocoding completed');
        fetchScrapedEvents();
      } else {
        toast.error(data.detail || 'Geocoding failed');
      }
    } catch (error) {
      toast.error('Error geocoding events');
    } finally {
      setScraping(false);
    }
  };

  const approveScrapedEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Event approved');
        fetchScrapedEvents();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to approve event');
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(`Error approving event: ${error.message || 'Network error'}`);
    }
  };

  const rejectScrapedEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Event rejected');
        fetchScrapedEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to reject event');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(`Error rejecting event: ${error.message || 'Network error'}`);
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      event_date: event.event_date || '',
      event_time: event.event_time || '',
      location: event.location || '',
      price: event.price || '',
      tag: event.tag || 'Community',
      image_url: event.image_url || '',
      linked_club_id: event.linked_club_id || '',
      ticket_url: event.ticket_url || '',
      ticket_widget_code: event.ticket_widget_code || ''
    });
  };

  const saveEventChanges = async () => {
    if (!editingEvent) return;
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${editingEvent.event_id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });
      if (response.ok) {
        toast.success('Event updated');
        setEditingEvent(null);
        fetchScrapedEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update event');
      }
    } catch (error) {
      toast.error('Error updating event');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCustomImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCustomImageUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setEditFormData(prev => ({ ...prev, image_url: data.url }));
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    } finally {
      setCustomImageUploading(false);
    }
  };

  const approveAllPending = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const pendingEvents = scrapedEvents.filter(e => e.status === 'pending');
      let approved = 0;
      for (const event of pendingEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${event.event_id}/approve`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          
        });
        if (response.ok) approved++;
      }
      toast.success(`Approved ${approved} events`);
      fetchScrapedEvents();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error('Error approving events');
    } finally {
      setScraping(false);
    }
  };

  const bulkApproveScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      let approved = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/approve`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          
        });
        if (response.ok) approved++;
      }
      toast.success(`Approved ${approved} events`);
      setSelectedScrapedEvents(new Set());
      fetchScrapedEvents();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error('Error approving events');
    } finally {
      setScraping(false);
    }
  };

  const bulkRejectScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      let rejected = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/reject`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          
        });
        if (response.ok) rejected++;
      }
      toast.success(`Rejected ${rejected} events`);
      setSelectedScrapedEvents(new Set());
      fetchScrapedEvents();
    } catch (error) {
      toast.error('Error rejecting events');
    } finally {
      setScraping(false);
    }
  };

  const bulkDeleteScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      let deleted = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          
        });
        if (response.ok) deleted++;
      }
      toast.success(`Deleted ${deleted} events`);
      setSelectedScrapedEvents(new Set());
      fetchScrapedEvents();
    } catch (error) {
      toast.error('Error deleting events');
    } finally {
      setScraping(false);
    }
  };

  const deletePastEvents = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/clear-past`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Past events deleted');
        fetchScrapedEvents();
      } else {
        toast.error(data.detail || 'Failed to delete past events');
      }
    } catch (error) {
      console.error('Delete past events error:', error);
      toast.error(`Error deleting past events: ${error.message || 'Network error'}`);
    } finally {
      setScraping(false);
    }
  };

  const findDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/duplicates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDuplicateGroups(data.duplicate_groups || []);
        setShowDuplicates(true);
        if (data.duplicate_groups?.length === 0) {
          toast.info('No duplicates found');
        }
      }
    } catch (error) {
      toast.error('Error finding duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const mergeDuplicateGroup = async (group) => {
    setMergingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      // Use the first event as primary, merge others into it
      const primaryId = group.event_ids[0];
      const duplicateIds = group.event_ids.slice(1);
      
      const params = new URLSearchParams();
      params.append('primary_event_id', primaryId);
      duplicateIds.forEach(id => params.append('duplicate_event_ids', id));
      
      const response = await fetch(`${API_URL}/api/admin/scraped-events/merge?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Duplicates merged successfully');
        setDuplicateGroups(prev => prev.filter(g => g.title !== group.title));
        fetchScrapedEvents();
      } else {
        toast.error(data.detail || 'Failed to merge duplicates');
      }
    } catch (error) {
      console.error('Merge error:', error);
      toast.error(`Error merging duplicates: ${error.message || 'Network error'}`);
    } finally {
      setMergingDuplicates(false);
    }
  };

  const deleteDuplicateGroup = async (group) => {
    setMergingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      // Keep first event, delete the rest
      const idsToDelete = group.event_ids.slice(1);
      
      let deletedCount = 0;
      for (const eventId of idsToDelete) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) deletedCount++;
      }
      
      toast.success(`Deleted ${deletedCount} duplicate events (kept 1)`);
      setDuplicateGroups(prev => prev.filter(g => g.title !== group.title));
      fetchScrapedEvents();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Error deleting duplicates: ${error.message || 'Network error'}`);
    } finally {
      setMergingDuplicates(false);
    }
  };

  const mergeAllDuplicates = async () => {
    setMergingDuplicates(true);
    try {
      for (const group of duplicateGroups) {
        const token = localStorage.getItem('token');
        const primaryId = group.event_ids[0];
        const duplicateIds = group.event_ids.slice(1);
        
        const params = new URLSearchParams();
        params.append('primary_event_id', primaryId);
        duplicateIds.forEach(id => params.append('duplicate_event_ids', id));
        
        await fetch(`${API_URL}/api/admin/scraped-events/merge?${params.toString()}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      toast.success('All duplicate groups merged');
      setDuplicateGroups([]);
      setShowDuplicates(false);
      fetchScrapedEvents();
    } catch (error) {
      toast.error('Error merging all duplicates');
    } finally {
      setMergingDuplicates(false);
    }
  };

  const autoLinkEvents = async () => {
    setLinkingEvents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/auto-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      const data = await response.json();
      if (response.ok) {
        if (data.suggestions?.length > 0) {
          setLinkSuggestions(data.suggestions);
          setShowLinkSuggestions(true);
        } else {
          toast.info('No link suggestions found');
        }
      }
    } catch (error) {
      toast.error('Error auto-linking events');
    } finally {
      setLinkingEvents(false);
    }
  };

  const confirmLink = async (eventId, clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/link/${clubId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        toast.success('Event linked to club');
        setLinkSuggestions(prev => prev.filter(s => s.event_id !== eventId));
        fetchScrapedEvents();
      }
    } catch (error) {
      toast.error('Error linking event');
    }
  };

  const rejectSuggestion = (eventId) => {
    setLinkSuggestions(prev => prev.filter(s => s.event_id !== eventId));
  };

  // Format a single event for Facebook posting
  const formatEventForFacebook = (event) => {
    const lines = [];
    lines.push('🎉 LINE DANCE EVENT 🎉');
    lines.push('');
    if (event.title) lines.push(`📌 ${event.title}`);
    if (event.location) lines.push(`📍 ${event.location}`);
    if (event.event_date) lines.push(`📅 ${event.event_date}`);
    if (event.event_time) lines.push(`🕐 ${event.event_time}`);
    if (event.price) lines.push(`💰 £${typeof event.price === 'number' ? event.price.toFixed(2) : event.price}`);
    if (event.description) {
      lines.push('');
      lines.push(event.description.substring(0, 200) + (event.description.length > 200 ? '...' : ''));
    }
    if (event.ticket_url) {
      lines.push('');
      lines.push(`🎟️ Tickets: ${event.ticket_url}`);
    }
    return lines.join('\n');
  };

  // Copy single event to clipboard
  const copyEventToClipboard = async (event) => {
    try {
      const text = formatEventForFacebook(event);
      await navigator.clipboard.writeText(text);
      toast.success('Event copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Copy multiple events to clipboard
  const copySelectedEventsToClipboard = async () => {
    try {
      const selectedEvents = filteredScrapedEvents.filter(e => selectedScrapedEvents.has(e.event_id));
      if (selectedEvents.length === 0) {
        toast.error('No events selected');
        return;
      }
      
      const formattedEvents = selectedEvents.map(formatEventForFacebook).join('\n\n━━━━━━━━━━━━━━━━━━━━\n\n');
      const header = `📣 UPCOMING LINE DANCE EVENTS (${selectedEvents.length})\n\n`;
      await navigator.clipboard.writeText(header + formattedEvents);
      toast.success(`${selectedEvents.length} events copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Copy all filtered events to clipboard
  const copyAllEventsToClipboard = async () => {
    try {
      if (filteredScrapedEvents.length === 0) {
        toast.error('No events to copy');
        return;
      }
      
      const formattedEvents = filteredScrapedEvents.map(formatEventForFacebook).join('\n\n━━━━━━━━━━━━━━━━━━━━\n\n');
      const header = `📣 UPCOMING LINE DANCE EVENTS (${filteredScrapedEvents.length})\n\n`;
      await navigator.clipboard.writeText(header + formattedEvents);
      toast.success(`${filteredScrapedEvents.length} events copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Filter events
  const filteredScrapedEvents = scrapedEvents.filter(event => {
    if (eventSourceFilter !== 'all' && (event.source_name || event.source) !== eventSourceFilter) return false;
    if (eventStatusFilter !== 'all' && event.status !== eventStatusFilter) return false;
    if (eventSearchQuery) {
      const query = eventSearchQuery.toLowerCase();
      return event.title?.toLowerCase().includes(query) || 
             event.location?.toLowerCase().includes(query);
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredScrapedEvents.length / eventsPerPage);
  const paginatedEvents = filteredScrapedEvents.slice(
    (currentPage - 1) * eventsPerPage,
    currentPage * eventsPerPage
  );

  // Get unique sources
  const sources = [...new Set(scrapedEvents.map(e => e.source).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Scheduler Status */}
      {schedulerStatus && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${schedulerStatus.status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <div>
                <p className="font-medium text-slate-900">
                  Automatic Scraping: {schedulerStatus.status === 'running' ? 'Active' : 'Stopped'}
                </p>
                <p className="text-sm text-slate-500">
                  {schedulerStatus.schedule}
                </p>
              </div>
            </div>
            {schedulerStatus.next_run && (
              <div className="text-sm text-slate-600">
                <Clock className="w-4 h-4 inline mr-1" />
                Next run: {new Date(schedulerStatus.next_run).toLocaleString('en-GB', { 
                  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                })}
              </div>
            )}
            <Button 
              onClick={runScheduledScrape} 
              disabled={scraping}
              className="bg-indigo-600 hover:bg-indigo-500"
            >
              {scraping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Run Now (Current + Next 2 Months)
            </Button>
          </div>
          {schedulerStatus.months_to_scrape && (
            <p className="text-xs text-slate-500 mt-2">
              Will scrape: {schedulerStatus.months_to_scrape.map(([m, y]) => `${m.charAt(0).toUpperCase() + m.slice(1)} ${y}`).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Scraper Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Manual Scraping</h2>
            <p className="text-sm text-slate-500 mt-1">Import events from external sources</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={scraperMonth} onValueChange={setScraperMonth}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {['january', 'february', 'march', 'april', 'may', 'june', 
                  'july', 'august', 'september', 'october', 'november', 'december'].map(m => (
                  <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={() => runScraper(scraperMonth)} 
              disabled={scraping}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="scrape-month-btn"
            >
              {scraping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Scrape {scraperMonth.charAt(0).toUpperCase() + scraperMonth.slice(1)}
            </Button>
            <Button 
              onClick={() => runScraper(null)} 
              disabled={scraping}
              variant="outline"
              data-testid="scrape-all-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Scrape All 2026
            </Button>
          </div>
        </div>
        
        {/* Backroad Boots Section */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-medium text-slate-900">Backroad Boots</h3>
              <p className="text-sm text-slate-500">Import events from Backroad Boots</p>
            </div>
            <Button 
              onClick={runBackroadBootsScraper} 
              disabled={scraping}
              variant="outline"
            >
              {scraping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Scrape Backroad Boots
            </Button>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="p-4 bg-slate-50 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <Button 
            onClick={copyAllEventsToClipboard} 
            disabled={filteredScrapedEvents.length === 0}
            variant="outline"
            size="sm"
            className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All for Facebook ({filteredScrapedEvents.length})
          </Button>
          <Button 
            onClick={geocodeAllEvents} 
            disabled={scraping}
            variant="outline"
            size="sm"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Geocode All
          </Button>
          <Button 
            onClick={approveAllPending} 
            disabled={scraping}
            variant="outline"
            size="sm"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve All Pending
          </Button>
          <Button 
            onClick={deletePastEvents} 
            disabled={scraping}
            variant="outline"
            size="sm"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Past Events
          </Button>
          <Button 
            onClick={findDuplicates} 
            disabled={loadingDuplicates}
            variant="outline"
            size="sm"
          >
            {loadingDuplicates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Find Duplicates
          </Button>
          <Button 
            onClick={autoLinkEvents} 
            disabled={linkingEvents}
            variant="outline"
            size="sm"
          >
            {linkingEvents ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Auto-Link to Clubs
          </Button>
        </div>
      </div>

      {/* Bulk Selection Actions */}
      {selectedScrapedEvents.size > 0 && (
        <div className="p-3 bg-violet-50 border-b border-violet-200 flex items-center justify-between">
          <span className="text-sm font-medium text-violet-800">
            {selectedScrapedEvents.size} event{selectedScrapedEvents.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={copySelectedEventsToClipboard}>
              <Copy className="w-4 h-4 mr-1" />
              Copy for Facebook
            </Button>
            <Button size="sm" onClick={bulkApproveScrapedEvents} disabled={scraping}>
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={bulkRejectScrapedEvents} disabled={scraping}>
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" disabled={scraping}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selectedScrapedEvents.size} Events</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the selected events. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={bulkDeleteScrapedEvents} className="bg-red-600 hover:bg-red-700">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button size="sm" variant="ghost" onClick={() => setSelectedScrapedEvents(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Duplicates Modal */}
      {showDuplicates && duplicateGroups.length > 0 && (
        <div className="p-4 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-amber-800">
              Duplicate Events Found ({duplicateGroups.length} groups)
            </h3>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={mergeAllDuplicates}
                disabled={mergingDuplicates}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {mergingDuplicates ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Merge All
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowDuplicates(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {duplicateGroups.map((group, idx) => (
              <div key={idx} className="p-3 bg-white rounded border border-amber-200 flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{group.title}</p>
                  <p className="text-xs text-slate-500">
                    {group.count} duplicates • {group.event_ids?.length || 0} events
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => mergeDuplicateGroup(group)}
                    disabled={mergingDuplicates}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Merge
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => deleteDuplicateGroup(group)}
                    disabled={mergingDuplicates}
                  >
                    Delete Dupes
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Suggestions Modal */}
      {showLinkSuggestions && linkSuggestions.length > 0 && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-blue-800">Club Link Suggestions</h3>
            <Button size="sm" variant="ghost" onClick={() => setShowLinkSuggestions(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {linkSuggestions.map((suggestion) => (
              <div key={suggestion.event_id} className="p-3 bg-white rounded border border-blue-200 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{suggestion.event_title}</p>
                  <p className="text-xs text-slate-500">→ {suggestion.suggested_club_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => confirmLink(suggestion.event_id, suggestion.suggested_club_id)}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => rejectSuggestion(suggestion.event_id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-4">
        <Input
          placeholder="Search events..."
          value={eventSearchQuery}
          onChange={(e) => setEventSearchQuery(e.target.value)}
          className="w-64"
        />
        <Select value={eventSourceFilter} onValueChange={setEventSourceFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {sources.map(source => (
              <SelectItem key={source} value={source}>{source}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventStatusFilter} onValueChange={setEventStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {filteredScrapedEvents.length} events
        </span>
      </div>

      {/* Events Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-10 px-4 py-3">
                <Checkbox
                  checked={paginatedEvents.length > 0 && selectedScrapedEvents.size === paginatedEvents.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedScrapedEvents(new Set(paginatedEvents.map(e => e.event_id)));
                    } else {
                      setSelectedScrapedEvents(new Set());
                    }
                  }}
                />
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Event</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Date</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Location</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Source</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedEvents.map((event) => (
              <tr key={event.event_id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedScrapedEvents.has(event.event_id)}
                    onCheckedChange={(checked) => {
                      const newSet = new Set(selectedScrapedEvents);
                      if (checked) {
                        newSet.add(event.event_id);
                      } else {
                        newSet.delete(event.event_id);
                      }
                      setSelectedScrapedEvents(newSet);
                    }}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {event.image_url && (
                      <img src={event.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900 line-clamp-1">{event.title}</p>
                      {event.linked_club_id && (
                        <Badge variant="outline" className="text-xs mt-1">
                          <Link2 className="w-3 h-3 mr-1" />
                          Linked
                        </Badge>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {event.event_date}
                  {event.event_time && <span className="text-slate-400 ml-1">{event.event_time}</span>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                  {event.location}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="text-xs">{event.source_name || event.source || 'Unknown'}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge 
                    className={
                      event.status === 'approved' ? 'bg-green-100 text-green-800' :
                      event.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }
                  >
                    {event.status}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-blue-600"
                      onClick={() => copyEventToClipboard(event)}
                      title="Copy for Facebook"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openEditModal(event)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {event.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-green-600"
                          onClick={() => approveScrapedEvent(event.event_id)}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => rejectScrapedEvent(event.event_id)}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={!!editingEvent} onOpenChange={() => setEditingEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input
                value={editFormData.title}
                onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Date</label>
                <Input
                  value={editFormData.event_date}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, event_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Time</label>
                <Input
                  value={editFormData.event_time}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, event_time: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Location</label>
              <Input
                value={editFormData.location}
                onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Price</label>
                <Input
                  value={editFormData.price}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, price: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tag</label>
                <Select 
                  value={editFormData.tag} 
                  onValueChange={(val) => setEditFormData(prev => ({ ...prev, tag: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTags.map(tag => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Link to Club</label>
              <Select 
                value={editFormData.linked_club_id || "none"} 
                onValueChange={(val) => setEditFormData(prev => ({ ...prev, linked_club_id: val === "none" ? "" : val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a club" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No club</SelectItem>
                  {clubs.map(club => (
                    <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Image</label>
              <div className="mt-2 space-y-2">
                {editFormData.image_url && (
                  <img src={editFormData.image_url} alt="" className="w-full h-40 object-cover rounded" />
                )}
                <div className="flex flex-wrap gap-2">
                  {defaultEventImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setEditFormData(prev => ({ ...prev, image_url: img.url }))}
                      className={`w-16 h-16 rounded border-2 overflow-hidden ${editFormData.image_url === img.url ? 'border-violet-500' : 'border-slate-200'}`}
                    >
                      <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-16 h-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-violet-500"
                  >
                    {customImageUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCustomImageUpload}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Ticket URL</label>
              <Input
                value={editFormData.ticket_url}
                onChange={(e) => setEditFormData(prev => ({ ...prev, ticket_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEvent(null)}>
              Cancel
            </Button>
            <Button onClick={saveEventChanges} disabled={isUpdating}>
              {isUpdating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminScraperTab;
